/**
 * **开关** —— 一个方框里有没有印。
 *
 * 不用对勾：那需要一个图标字体，而图标字体是明确的反模式
 * （`visual-design.md` §8）。"开着"就是**这个块被印上了**。
 *
 * 状态反馈走 `state.ts`：hover 加印一版，禁用撤墨 + 注销杠。
 */

import { Container, Graphics } from 'pixi.js';
import { INK, uiPad } from './style';
import { createSurface, drawRedactionSlash, drawStamp, type ControlMotion, type ControlState } from './state';
import { stampInkAt } from './ink';
import { richLabel } from './richText';
import type { Widget } from './base';

export interface ToggleSpec {
  readonly label: string;
  readonly scale: number;
  readonly value: boolean;
  readonly onChange: (value: boolean) => void;
  readonly enabled?: boolean;
  readonly width?: number;
  /** 开关右边的补充说明 */
  readonly note?: string;
}

export interface Toggle extends Widget {
  setValue(value: boolean): void;
  setEnabled(enabled: boolean): void;
  readonly value: boolean;
}

export function toggle(spec: ToggleSpec): Toggle {
  const scale = spec.scale;
  const box = uiPad(16, scale);

  const container = new Container();
  const content = new Container();
  const mark = new Graphics();
  const text = richLabel({ text: spec.label, scale, size: 13, fill: INK.text, role: 'cjk' });
  const note =
    spec.note === undefined
      ? null
      : richLabel({ text: spec.note, scale, size: 11, fill: INK.textDim, role: 'cjk' });

  content.addChild(mark, text);
  if (note !== null) content.addChild(note);
  container.addChild(content);

  const width = spec.width ?? 320;
  /**
   * ⚠️ **高度必须把 note 算进去。**
   *
   * 这条踩过一次：`height` 只报了那一行，而 note 画在它下面 ——
   * 于是竖排版面的游标按 `height` 往前走，**下一个控件正好压在 note 上**。
   * 判据很简单：**控件报的高度必须等于它真正占据的高度**。
   */
  const rowHeight = Math.max(box, text.height);
  const noteGap = note === null ? 0 : uiPad(2, scale);
  const height = rowHeight + (note === null ? 0 : noteGap + note.height);
  const boxY = Math.round((rowHeight - box) / 2);
  text.position.set(box + uiPad(10, scale), Math.round((rowHeight - text.height) / 2));
  if (note !== null) note.position.set(box + uiPad(10, scale), rowHeight + noteGap);

  let value = spec.value;

  const surface = createSurface({
    container,
    width,
    height,
    ...(spec.enabled === undefined ? {} : { enabled: spec.enabled }),
    onPress: () => {
      value = !value;
      paint(surface.state, surface.motion);
      spec.onChange(value);
    },
    onStateChange: (state, motion) => paint(state, motion),
    onFrame: (state, motion) => paint(state, motion),
    focusable: true,
  });

  function paint(state: ControlState, motion: ControlMotion): void {
    // 方块：开着 = 实心酸色章；关着 = 只描边
    const variant = value ? 'primary' : 'secondary';
    const ink = stampInkAt(variant, state, motion.mix);
    mark.clear();
    drawStamp(mark, ink, { y: boxY, width: box, height: box }, scale, motion.lift);
    if (state === 'disabled') {
      drawRedactionSlash(mark, { y: boxY, width: box, height: box }, scale, INK.rule, motion.lift);
    }

    // 标签：开着是正文色，关着暗一档。禁用时一律撤到 dim
    const labelInk = stampInkAt('quiet', state, motion.mix).label;
    text.style.fill = state === 'disabled' ? labelInk : value ? INK.text : INK.textDim;
    if (note !== null) note.style.fill = INK.textDim;

    content.position.set(0, motion.lift);
  }

  paint(surface.state, surface.motion);

  return {
    container,
    width,
    height,
    get value() {
      return value;
    },
    setValue(next: boolean) {
      value = next;
      paint(surface.state, surface.motion);
    },
    setEnabled(next: boolean) {
      surface.setEnabled(next);
    },
  };
}
